import { reactive, ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { isServer, isEditorActive } from '@sitecore-jss/sitecore-jss/utils';
import type { MenuItem } from '@/types/menu';

interface BackdropOwner {
  global: boolean;
}

export const useAppStore = defineStore('app', () => {
  const bodyOffset = ref(0);
  const enterpriseMenusCache = ref<Record<string, { menus: MenuItem[] }>>({});
  const isMapApiLoaded = ref(false);
  const isLocalitiesApiLoaded = ref(false);
  const isSmoothScrollDisabled = ref(false);
  const backdropOwners = reactive(new Map<string, BackdropOwner>());
  const backdrop = computed(() => ({
    isEnabled: backdropOwners.size > 0,
    isGlobal: [...backdropOwners.values()].some((v) => v.global),
  }));
  const scrollPosition = ref<number | null>(null);
  const guestlineActiveUrl = ref<string | null>(null);
  const scrollBlockCount = ref(0);

  const isScrollOnBodyDisabled = computed(() => scrollBlockCount.value > 0);

  const setGuestlineActiveUrl = (url: string | null) => {
    guestlineActiveUrl.value = url ? url + '&enableCustomFrameAncestor=true' : null;
  };

  const setBodyOffset = (newVal: number) => {
    if (isServer() || bodyOffset.value === newVal) return;
    bodyOffset.value = newVal;
    document.body.style.setProperty('--body-offset', newVal ? `${newVal}px` : 'unset');
  };

  const setMapApiLoaded = () => {
    isMapApiLoaded.value = true;
  };

  const setLocalitiesApiLoaded = () => {
    isLocalitiesApiLoaded.value = true;
  };

  const toggleBodyClass = (className: string, enable: boolean) => {
    document.body.classList.toggle(className, enable);
  };

  const disableScrollOnBody = (newVal: boolean) => {
    if (isServer() || isEditorActive()) return;
    scrollBlockCount.value = Math.max(scrollBlockCount.value + (newVal ? 1 : -1), 0);
    toggleBodyClass('disable-body-scroll', !!scrollBlockCount.value);
  };

  const disableSmoothScroll = (newVal: boolean) => {
    if (isServer() || isSmoothScrollDisabled.value === newVal) return;
    isSmoothScrollDisabled.value = newVal;
    toggleBodyClass('disable-smooth-scroll', newVal);
  };

  const setBackdrop = (
    ownerId: string,
    { enable = false, global = false }: { enable?: boolean; global?: boolean }
  ) => {
    if (isServer()) return;
    if (enable) {
      backdropOwners.set(ownerId, { global });
    } else {
      backdropOwners.delete(ownerId);
    }
  };

  watch(backdrop, ({ isEnabled, isGlobal }) => {
    if (isServer()) return;
    toggleBodyClass('backdrop', isEnabled);
    toggleBodyClass('backdrop--global', isEnabled && isGlobal);
  });

  const blockScrolling = () => {
    if (isServer() || isEditorActive()) return;
    scrollPosition.value = window.scrollY;
    disableScrollOnBody(true);
    document.body.style.top = `-${scrollPosition.value}px`;
  };

  const unblockScrolling = () => {
    if (isServer() || isEditorActive()) return;
    disableScrollOnBody(false);
    document.body.style.removeProperty('top');
    window.scrollTo({ top: scrollPosition.value, left: 0, behavior: 'instant' });
  };

  const updateScrollingPosition = (newPosition: number) => {
    scrollPosition.value = newPosition;
    unblockScrolling();
    blockScrolling();
  };

  const getCachedMenus = (cacheKey: string) => {
    if (!cacheKey) return undefined;
    return enterpriseMenusCache.value[cacheKey];
  };

  const setCachedMenus = (cacheKey: string, data: { menus: MenuItem[] }) => {
    if (!cacheKey) return;

    enterpriseMenusCache.value = {
      ...enterpriseMenusCache.value,
      [cacheKey]: data,
    };
  };

  return {
    bodyOffset,
    enterpriseMenusCache,
    isMapApiLoaded,
    isLocalitiesApiLoaded,
    isScrollOnBodyDisabled,
    isSmoothScrollDisabled,
    backdrop,
    scrollPosition,
    guestlineActiveUrl,
    setBodyOffset,
    setMapApiLoaded,
    setLocalitiesApiLoaded,
    disableScrollOnBody,
    disableSmoothScroll,
    setBackdrop,
    blockScrolling,
    unblockScrolling,
    updateScrollingPosition,
    getCachedMenus,
    setCachedMenus,
    setGuestlineActiveUrl,
  };
});
